import { motion } from 'framer-motion'
import { 
  ArrowLeft, 
  LogOut,
  Building2,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  BarChart3,
  MapPin,
  FileText,
  Calendar,
  Phone,
  MessageSquare,
  Shield,
  Zap,
  Target,
  Activity,
  Brain,
  Eye,
  ThumbsUp,
  Home,
  TreePine,
  Car
} from 'lucide-react'
import InfoTooltip from '../common/InfoTooltip'

export default function RegidorDashboard({ onBack, onLogout, businessData }) {
  const kpis = [
    {
      title: 'Eficiencia Gestión',
      value: '78%',
      change: '+15%',
      trend: 'up',
      icon: Target,
      color: 'green',
      tooltip: {
        title: 'Eficiencia en Gestión Municipal',
        description: 'Porcentaje de eficiencia en la gestión de servicios públicos, medido por optimización de recursos y cumplimiento de objetivos.',
        aiMethod: 'Análisis de KPIs municipales, optimización de rutas de servicios, predicción de demanda ciudadana.',
        dataSource: 'Datos de servicios públicos, reportes de cumplimiento, encuestas ciudadanas, indicadores municipales.',
        updateFrequency: 'Evaluación mensual con monitoreo continuo'
      }
    },
    {
      title: 'Satisfacción Ciudadana',
      value: '82%',
      change: '+8%',
      trend: 'up',
      icon: ThumbsUp,
      color: 'blue',
      tooltip: {
        title: 'Índice de Satisfacción Ciudadana',
        description: 'Medición de la satisfacción de los ciudadanos con los servicios públicos municipales basada en encuestas y feedback.',
        aiMethod: 'Análisis de sentimientos en redes sociales, procesamiento de encuestas, scoring automático de satisfacción.',
        dataSource: 'Encuestas ciudadanas, redes sociales, reportes de quejas, feedback de servicios públicos.',
        updateFrequency: 'Análisis continuo con reporte mensual'
      }
    },
    {
      title: 'Tiempo Respuesta',
      value: '24h',
      change: '-12h',
      trend: 'up',
      icon: Clock,
      color: 'purple',
      description: 'Promedio',
      tooltip: {
        title: 'Tiempo Promedio de Respuesta',
        description: 'Tiempo promedio que toma responder y resolver solicitudes ciudadanas, optimizado mediante priorización inteligente.',
        aiMethod: 'Algoritmos de priorización automática, predicción de tiempos de resolución, optimización de asignación de recursos.',
        dataSource: 'Sistema de tickets ciudadanos, tiempos históricos de resolución, complejidad de solicitudes.',
        updateFrequency: 'Medición en tiempo real con promedio diario'
      }
    },
    {
      title: 'Solicitudes Resueltas',
      value: '456',
      change: '+89',
      trend: 'up',
      icon: CheckCircle,
      color: 'orange',
      suffix: '/mes',
      tooltip: {
        title: 'Resolución de Solicitudes Ciudadanas',
        description: 'Número de solicitudes ciudadanas resueltas exitosamente utilizando sistemas de gestión inteligente.',
        aiMethod: 'Automatización de procesos, clasificación inteligente de solicitudes, optimización de flujos de trabajo.',
        dataSource: 'Sistema de gestión ciudadana, base de datos de solicitudes, registros de resolución.',
        updateFrequency: 'Conteo en tiempo real con consolidación mensual'
      }
    }
  ]

  const publicServices = [
    {
      area: 'Servicios Públicos',
      requests: 234,
      resolved: 198,
      efficiency: 85,
      avgTime: '18h',
      satisfaction: 79,
      color: 'blue'
    },
    {
      area: 'Obras Públicas',
      requests: 89,
      resolved: 76,
      efficiency: 85,
      avgTime: '5.2 días',
      satisfaction: 81,
      color: 'green'
    },
    {
      area: 'Seguridad Ciudadana',
      requests: 156,
      resolved: 134,
      efficiency: 86,
      avgTime: '4.1h',
      satisfaction: 88,
      color: 'red'
    },
    {
      area: 'Medio Ambiente',
      requests: 67,
      resolved: 59,
      efficiency: 88,
      avgTime: '2.3 días',
      satisfaction: 85,
      color: 'green'
    }
  ]

  const citizenInsights = [
    {
      district: 'Centro Histórico',
      population: 15420,
      mainIssues: ['Tráfico', 'Ruido', 'Mantenimiento'],
      satisfaction: 75,
      priority: 'high',
      trend: '+3%'
    },
    {
      district: 'Zona Norte',
      population: 23840,
      mainIssues: ['Alumbrado', 'Seguridad', 'Parques'],
      satisfaction: 83,
      priority: 'medium',
      trend: '+7%'
    },
    {
      district: 'Zona Sur',
      population: 18670,
      mainIssues: ['Agua', 'Pavimento', 'Transporte'],
      satisfaction: 79,
      priority: 'medium',
      trend: '+5%'
    }
  ]

  const recentRequests = [
    {
      type: 'urgent',
      title: 'Fuga de agua - Col. Independencia',
      citizen: 'María González',
      time: 'Hace 2h',
      status: 'En proceso',
      aiPriority: 'Alta'
    },
    {
      type: 'normal',
      title: 'Solicitud poda de árboles',
      citizen: 'Juan Pérez',
      time: 'Hace 4h',
      status: 'Asignado',
      aiPriority: 'Media'
    },
    {
      type: 'info',
      title: 'Consulta sobre trámites',
      citizen: 'Ana Martín',
      time: 'Hace 6h',
      status: 'Respondido',
      aiPriority: 'Baja'
    }
  ]

  const monthlyStats = [
    { month: 'Ene', requests: 420, resolved: 378, satisfaction: 76 },
    { month: 'Feb', requests: 445, resolved: 401, satisfaction: 78 },
    { month: 'Mar', requests: 398, resolved: 367, satisfaction: 80 },
    { month: 'Abr', requests: 467, resolved: 423, satisfaction: 79 },
    { month: 'May', requests: 512, resolved: 468, satisfaction: 81 },
    { month: 'Jun', requests: 489, resolved: 456, satisfaction: 82 }
  ]

  const aiInsights = [
    {
      type: 'prediction',
      message: 'Se prevé aumento 25% en solicitudes de bacheo próxima semana',
      confidence: 87,
      action: 'Preparar recursos'
    },
    {
      type: 'optimization',
      message: 'Reasignar 2 cuadrillas a Zona Norte mejorará eficiencia 15%',
      confidence: 92,
      action: 'Revisar asignación'
    },
    {
      type: 'alert',
      message: 'Patrón de quejas recurrentes en alumbrado - Col. Morelos',
      confidence: 95,
      action: 'Inspección requerida'
    }
  ]

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      purple: 'bg-purple-100 text-purple-800',
      orange: 'bg-orange-100 text-orange-800',
      red: 'bg-red-100 text-red-800'
    }
    return colors[color] || colors.blue
  }

  const getIconColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
      red: 'bg-red-500'
    }
    return colors[color] || colors.blue
  }

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    }
    return colors[priority] || colors.medium
  }

  const getRequestTypeColor = (type) => {
    const colors = {
      urgent: 'bg-red-100',
      normal: 'bg-blue-100',
      info: 'bg-green-100'
    }
    return colors[type] || colors.normal
  }

  const getRequestTypeIcon = (type) => {
    if (type === 'urgent') return AlertTriangle
    if (type === 'normal') return MessageSquare
    return FileText
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={onBack}
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors mr-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </button>
              <div className="flex items-center">
                <div className={`w-8 h-8 bg-gradient-to-br ${businessData.color} rounded-lg flex items-center justify-center mr-3`}>
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{businessData.title}</h1>
                  <p className="text-sm text-gray-500">Gestión Pública Inteligente</p>
                </div>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* KPIs Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {kpis.map((kpi, index) => {
            const IconComponent = kpi.icon
            return (
              <div key={index} className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm text-gray-600">{kpi.title}</p>
                      <InfoTooltip 
                        title={kpi.tooltip.title}
                        description={kpi.tooltip.description}
                        aiMethod={kpi.tooltip.aiMethod}
                        dataSource={kpi.tooltip.dataSource}
                        updateFrequency={kpi.tooltip.updateFrequency}
                        iconColor="text-gray-400"
                      />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {kpi.value}{kpi.suffix || ''}
                    </p>
                    <div className="flex items-center mt-2">
                      <span className={`text-sm font-medium ${kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                        {kpi.change}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">
                        {kpi.description || 'vs mes anterior'}
                      </span>
                    </div>
                  </div>
                  <div className={`p-3 ${getIconColorClasses(kpi.color)} rounded-lg`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Servicios Públicos */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-semibold text-gray-900">Gestión de Servicios Públicos</h3>
                  <InfoTooltip 
                    title="Sistema de Gestión Inteligente de Servicios"
                    description="Plataforma de IA que optimiza la gestión de servicios públicos municipales mediante análisis de datos y predicción de demanda."
                    aiMethod="Análisis predictivo de demanda, optimización de rutas, gestión automática de recursos, scoring de eficiencia."
                    dataSource="Datos de servicios públicos, reportes ciudadanos, sensores IoT, historial de mantenimiento."
                    updateFrequency="Monitoreo continuo con reportes diarios"
                    iconColor="text-gray-400"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Brain className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-gray-600">IA Ciudadana</span>
                </div>
              </div>
              
              <div className="space-y-6">
                {publicServices.map((service, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{service.area}</h4>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getColorClasses(service.color)}`}>
                        {service.efficiency}% eficiencia
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4 text-center mb-3">
                      <div>
                        <p className="text-sm text-gray-600">Solicitudes</p>
                        <p className="text-lg font-bold text-gray-900">{service.requests}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Resueltas</p>
                        <p className="text-lg font-bold text-green-600">{service.resolved}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Tiempo Prom.</p>
                        <p className="text-lg font-bold text-gray-900">{service.avgTime}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Satisfacción</p>
                        <p className="text-lg font-bold text-blue-600">{service.satisfaction}%</p>
                      </div>
                    </div>
                    
                    <div className="bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${getIconColorClasses(service.color)}`}
                        style={{ width: `${(service.resolved / service.requests) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Análisis Mensual */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Tendencia de Solicitudes y Satisfacción
                </h3>
                <InfoTooltip 
                  title="Análisis de Tendencias Ciudadanas"
                  description="Seguimiento histórico de solicitudes ciudadanas y niveles de satisfacción con análisis predictivo de tendencias."
                  aiMethod="Análisis de series temporales, predicción de volumen de solicitudes, análisis de patrones estacionales."
                  dataSource="Base de datos de solicitudes históricas, encuestas de satisfacción, análisis de resolución."
                  updateFrequency="Análisis mensual con predicciones trimestrales"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {monthlyStats.map((stat, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="w-12 text-sm font-medium text-gray-700">{stat.month}</div>
                    
                    <div className="flex-1 grid grid-cols-3 gap-4">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600">Solicitudes</span>
                          <span className="text-sm font-medium">{stat.requests}</span>
                        </div>
                        <div className="bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-blue-500"
                            style={{ width: `${(stat.requests / 600) * 100}%` }}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600">Resueltas</span>
                          <span className="text-sm font-medium">{stat.resolved}</span>
                        </div>
                        <div className="bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-green-500"
                            style={{ width: `${(stat.resolved / 600) * 100}%` }}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600">Satisfacción</span>
                          <span className="text-sm font-medium">{stat.satisfaction}%</span>
                        </div>
                        <div className="bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-purple-500"
                            style={{ width: `${stat.satisfaction}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Mejora continua:</strong> La eficiencia ha aumentado 18% en 6 meses 
                  gracias al análisis predictivo de demanda y optimización de recursos.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Solicitudes Recientes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Solicitudes Recientes</h3>
                <InfoTooltip 
                  title="Sistema de Solicitudes Ciudadanas"
                  description="Gestión inteligente de solicitudes ciudadanas con priorización automática y seguimiento de resolución."
                  aiMethod="Clasificación automática por urgencia, análisis de contenido, predicción de tiempos de resolución."
                  dataSource="Sistema de tickets ciudadanos, historial de solicitudes, patrones de resolución."
                  updateFrequency="Actualización en tiempo real"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {recentRequests.map((request, index) => {
                  const IconComponent = getRequestTypeIcon(request.type)
                  return (
                    <div key={index} className="border border-gray-100 rounded-lg p-3">
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-full flex-shrink-0 ${getRequestTypeColor(request.type)}`}>
                          <IconComponent className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-gray-900">{request.title}</h4>
                          <p className="text-xs text-gray-600 mt-1">Por: {request.citizen}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-gray-500">{request.time}</span>
                            <div className="flex space-x-2">
                              <span className="text-xs bg-gray-100 px-2 py-1 rounded">{request.status}</span>
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                IA: {request.aiPriority}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </motion.div>

            {/* Análisis por Distrito */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Análisis por Distrito</h3>
                <InfoTooltip 
                  title="Análisis Territorial Inteligente"
                  description="Análisis detallado por distrito para identificar necesidades específicas, prioridades y oportunidades de mejora."
                  aiMethod="Análisis geoespacial, segmentación automática por demografía, scoring de necesidades por zona."
                  dataSource="Datos demográficos, reportes ciudadanos por ubicación, indicadores socioeconómicos, servicios por distrito."
                  updateFrequency="Análisis trimestral con monitoreo mensual"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {citizenInsights.map((district, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{district.district}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(district.priority)}`}>
                        {district.priority === 'high' ? 'Alta' : district.priority === 'medium' ? 'Media' : 'Baja'}
                      </span>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="text-sm">
                        <span className="text-gray-600">Población:</span>
                        <span className="ml-1 font-medium">{district.population.toLocaleString()}</span>
                      </div>
                      
                      <div className="text-sm">
                        <span className="text-gray-600">Principales Issues:</span>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {district.mainIssues.map((issue, idx) => (
                            <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                              {issue}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="text-sm">
                          <span className="text-gray-600">Satisfacción:</span>
                          <span className="ml-1 font-medium">{district.satisfaction}%</span>
                        </div>
                        <span className="text-sm font-medium text-green-600">{district.trend}</span>
                      </div>
                      
                      <div className="bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            district.satisfaction >= 80 ? 'bg-green-500' :
                            district.satisfaction >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${district.satisfaction}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Insights de IA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Insights Predictivos IA</h3>
              
              <div className="space-y-3">
                {aiInsights.map((insight, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-3">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-blue-100 rounded-full flex-shrink-0">
                        <Brain className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{insight.message}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">Confianza: {insight.confidence}%</span>
                          <button className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                            {insight.action}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  )
} 